import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, GuildInviteResponse } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

interface UseGuildInvitesReturn {
  invites: GuildInviteResponse[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
  create: (options?: { maxUses?: number; expiresInHours?: number }) => Promise<GuildInviteResponse>;
  revoke: (inviteId: string) => Promise<void>;
}

export function useGuildInvites(guildId: string | undefined): UseGuildInvitesReturn {
  const queryClient = useQueryClient();
  const invitesQueryKey = queryKeys.guilds.invites(guildId!);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: invitesQueryKey,
    queryFn: () => api.listGuildInvites(guildId!),
    enabled: !!guildId,
  });

  const createMut = useMutation({
    mutationFn: (options?: { maxUses?: number; expiresInHours?: number }) =>
      api.createGuildInvite(guildId!, options),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invitesQueryKey });
    },
  });

  const revokeMut = useMutation({
    mutationFn: (inviteId: string) => api.revokeGuildInvite(guildId!, inviteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invitesQueryKey });
    },
  });

  return {
    invites: data?.invites ?? [],
    loading: isLoading,
    error: error instanceof Error ? error.message : error ? 'Failed to load invites' : null,
    refresh: () => {
      refetch();
    },
    create: async (options?) => {
      if (!guildId) throw new Error('No guild ID');
      return createMut.mutateAsync(options);
    },
    revoke: async (inviteId: string) => {
      if (!guildId) return;
      await revokeMut.mutateAsync(inviteId);
    },
  };
}
